import { err, ok, Result } from 'neverthrow';
import { applyRelColsInOrder } from './apply-rel-cols-in-order';
import { ParseCtxFor } from './eraql-expr-checker';
import { resolveRelCols } from './resolve-rel-cols';
import { resolveRelSource } from './resolve-rel-source';
import {
  GraphError,
  Identity,
  IToolResolver,
  Item,
  PartialSuccess,
  RelItem,
  RelNode,
  ResolutionOrder,
} from './types';
import _ from 'lodash';
import { Eq, Expression, From, Relation } from '@cotera/era';
import { SYSTEM_TABLES } from '../../runtime/system-tables';

export const resolveRel = (
  node: RelNode,
  opts: {
    readonly resolutionOrder: ResolutionOrder;
    readonly ctxFor: ParseCtxFor;
    readonly toolResolver: IToolResolver;
  }
): Result<RelItem, { err: GraphError; partial: PartialSuccess | null }> => {
  const { source, where, pk, cols, meta } = node;
  const { identity: relIdent } = meta;
  const { ctxFor, resolutionOrder, toolResolver } = opts;

  const relRes = resolveRelSource({
    source,
    pk,
    where,
    ctxFor,
  });

  if (relRes.isErr()) {
    return err({ err: relRes.error, partial: null });
  }

  const { filteredRel, pkRes, whereRes } = relRes.value;

  const colRes = resolveRelCols({
    rel: filteredRel,
    relIdent,
    ctxFor,
    pkRes,
    resolutionOrder,
    cols,
    toolResolver,
  });

  if (colRes.isErr()) {
    return err({
      err: colRes.error.err,
      partial: {
        t: 'rel',
        attributes: { ...filteredRel.attributes, ...colRes.error.attributes },
      },
    });
  }

  const successes = colRes.value;

  const rel = filteredRel.andThen((rel) =>
    applyRelColsInOrder({ base: rel, relIdent, cols: successes })
  );

  const item: Item = {
    t: 'rel',
    identity: relIdent,
    relNode: node,
    rel: pkRes !== null ? joinSystemColumns(relIdent, rel, pkRes) : rel,
    pk: pkRes,
    where: whereRes,
    cols: _.mapValues(successes, ({ eventStream, meta, identity }) => ({
      identity,
      meta,
      eventStream,
    })),
  };

  return ok(item);
};

/**
 * Joins the system columns `__cotera_stable_id` and `__pk`.
 */
const joinSystemColumns = (
  relIdent: Identity,
  baseRel: Relation,
  pkExpr: Expression
): Relation => {
  const stableIds = From(SYSTEM_TABLES.stable_ids).where((t) =>
    t.attr('definition_id').eq(relIdent.toString())
  );

  const pkAsString = pkExpr.andThen((x) =>
    x.implements('string') ? x : x.cast('string')
  );
  return baseRel
    .select((t) => ({
      ...t.star(),
      __pk: pkAsString,
    }))
    .leftJoin(stableIds, (base, sids) => ({
      on: Eq(sids.attr('identifier'), base.attr('__pk')),
      select: {
        ...base.star(),
        __cotera_stable_id: sids.attr('cotera_stable_id'),
      },
    }));
};
